import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { getSprintPeriods, getCurrentSprintIndex } from './utils/sprintUtils';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AREA_PATH = 'Fabrique';
const TYPE_FIELD_NAME = 'Custom.Typededemande';
const AUTOMATED_TESTS_STATUS_FIELD_NAME = 'Custom.AutomatedTestsStatus';
const AUTOMATED_KPI_START_DATE = '2025-01-01';
const AUTOMATED_KPI_MIN_STATE = '4- Doc';
const SPRINT_START_DATE = new Date('2025-11-05T00:00:00');
const SPRINT_START_NUMBER = 23;
const SPRINT_COUNT = 8;

function toDateOnly(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export default function KpiDemandesClient() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const [bySprint, setBySprint] = useState(null);
  const [byStatus, setByStatus] = useState(null);
  const [byType, setByType] = useState(null);
  const [byAreaPath, setByAreaPath] = useState(null);
  const [mttrByType, setMttrByType] = useState(null);
  const [prTypeStats, setPrTypeStats] = useState(null);
  const [automatedCoverageStats, setAutomatedCoverageStats] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const currentSprintIndex = getCurrentSprintIndex(SPRINT_START_DATE);
        const firstSprintIndex = Math.max(0, currentSprintIndex - (SPRINT_COUNT - 1));
        const sprints = getSprintPeriods(SPRINT_START_DATE, firstSprintIndex, SPRINT_COUNT, SPRINT_START_NUMBER);

        const startDate = sprints[0].start.toISOString().split('T')[0];
        const [dataForSprint, dataAll, automatedKpiItems] = await Promise.all([
          azureDevOpsService.getDemandeClientWorkItems({
            startDate,
            areaPath: AREA_PATH,
            typeFieldName: TYPE_FIELD_NAME
          }),
          azureDevOpsService.getDemandeClientWorkItems({
            areaPath: AREA_PATH,
            typeFieldName: TYPE_FIELD_NAME
          }),
          azureDevOpsService.getDemandeClientWorkItems({
            startDate: AUTOMATED_KPI_START_DATE,
            minState: AUTOMATED_KPI_MIN_STATE,
            automatedTestsStatusFieldName: AUTOMATED_TESTS_STATUS_FIELD_NAME
          })
        ]);

        setItems(dataAll);

        const sprintCounts = sprints.map(sprint => {
          const count = dataForSprint.filter(item => {
            const created = toDateOnly(item.createdDate);
            return created && created >= sprint.start && created <= sprint.end;
          }).length;
          return { sprint: sprint.name, count };
        });

        setBySprint({
          labels: sprintCounts.map(row => row.sprint),
          datasets: [
            {
              label: 'Demandes client creees',
              data: sprintCounts.map(row => row.count),
              backgroundColor: 'rgba(59, 130, 246, 0.7)'
            }
          ]
        });

        const statusCounts = {};
        dataAll.forEach(item => {
          const status = item.state || 'Non renseigne';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const statusLabels = Object.keys(statusCounts).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

        setByStatus({
          labels: statusLabels,
          datasets: [
            {
              label: 'Demandes client par statut',
              data: statusLabels.map(label => statusCounts[label]),
              backgroundColor: 'rgba(16, 185, 129, 0.7)'
            }
          ]
        });

        const typeCounts = {};
        dataAll.forEach(item => {
          const typeValue = item.type ? String(item.type) : 'Non renseigne';
          typeCounts[typeValue] = (typeCounts[typeValue] || 0) + 1;
        });

        const typeLabels = Object.keys(typeCounts).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        setByType({
          labels: typeLabels,
          datasets: [
            {
              label: 'Demandes client par type',
              data: typeLabels.map(label => typeCounts[label]),
              backgroundColor: 'rgba(234, 179, 8, 0.7)'
            }
          ]
        });

        const areaCounts = {};
        dataAll.forEach(item => {
          const area = item.areaPath || 'Non renseigne';
          areaCounts[area] = (areaCounts[area] || 0) + 1;
        });

        const areaLabels = Object.keys(areaCounts).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        setByAreaPath({
          labels: areaLabels,
          datasets: [
            {
              label: 'Demandes client par area path',
              data: areaLabels.map(label => areaCounts[label]),
              backgroundColor: 'rgba(99, 102, 241, 0.7)'
            }
          ]
        });

        const closedStates = new Set(['6- Done', '6- Cancelled']);
        const mttrBuckets2025 = {};
        const mttrBuckets2026 = {};

        dataAll.forEach(item => {
          if (!closedStates.has(item.state)) return;
          if (!item.closedDate || !item.createdDate) return;

          const created = toDateOnly(item.createdDate);
          const closed = toDateOnly(item.closedDate);
          if (!created || !closed) return;

          const days = (closed - created) / (1000 * 60 * 60 * 24);
          if (days < 0) return;

          const createdYear = created.getFullYear();
          const typeValue = item.type ? String(item.type) : 'Non renseigne';
          
          const buckets = createdYear === 2025 ? mttrBuckets2025 : createdYear === 2026 ? mttrBuckets2026 : null;
          if (!buckets) return;

          if (!buckets[typeValue]) {
            buckets[typeValue] = { total: 0, count: 0 };
          }
          buckets[typeValue].total += days;
          buckets[typeValue].count += 1;
        });

        const allTypes = new Set([...Object.keys(mttrBuckets2025), ...Object.keys(mttrBuckets2026)]);
        const mttrLabels = Array.from(allTypes).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        
        const mttrValues2025 = mttrLabels.map(label => {
          const bucket = mttrBuckets2025[label];
          return bucket && bucket.count ? Number((bucket.total / bucket.count).toFixed(1)) : 0;
        });
        const mttrCounts2025 = mttrLabels.map(label => mttrBuckets2025[label]?.count || 0);

        const mttrValues2026 = mttrLabels.map(label => {
          const bucket = mttrBuckets2026[label];
          return bucket && bucket.count ? Number((bucket.total / bucket.count).toFixed(1)) : 0;
        });
        const mttrCounts2026 = mttrLabels.map(label => mttrBuckets2026[label]?.count || 0);

        setMttrByType({
          labels: mttrLabels,
          counts2025: mttrCounts2025,
          counts2026: mttrCounts2026,
          datasets: [
            {
              label: 'MTTR 2025 (jours)',
              data: mttrValues2025,
              backgroundColor: 'rgba(59, 130, 246, 0.7)'
            },
            {
              label: 'MTTR 2026 (jours)',
              data: mttrValues2026,
              backgroundColor: 'rgba(16, 185, 129, 0.7)'
            }
          ]
        });

        const doneIds = await azureDevOpsService.getLastDoneDemandeClientIds(30, AREA_PATH);
        const prCounts = { back: 0, front: 0, devops: 0 };

        for (const id of doneIds) {
          const prs = await azureDevOpsService.getLinkedPullRequestsForWorkItem(id);
          const flags = { back: false, front: false, devops: false };

          prs.forEach(pr => {
            const repoName = String(pr.repositoryName || '').toLowerCase();
            if (repoName === 'argonos-front') {
              flags.front = true;
            } else if (repoName === 'shared-devops') {
              flags.devops = true;
            } else if (repoName === 'argonos') {
              flags.back = true;
            }
          });

          if (flags.back) prCounts.back += 1;
          if (flags.front) prCounts.front += 1;
          if (flags.devops) prCounts.devops += 1;
        }

        const totalDone = doneIds.length || 0;
        const prPercentages = {
          back: totalDone ? Number(((prCounts.back / totalDone) * 100).toFixed(1)) : 0,
          front: totalDone ? Number(((prCounts.front / totalDone) * 100).toFixed(1)) : 0,
          devops: totalDone ? Number(((prCounts.devops / totalDone) * 100).toFixed(1)) : 0
        };

        setPrTypeStats({ total: totalDone, counts: prCounts, percentages: prPercentages });

        const prLinkedDemandes = await Promise.all(
          automatedKpiItems.map(async item => {
            const prs = await azureDevOpsService.getLinkedPullRequestsForWorkItem(item.id);
            return {
              ...item,
              hasPullRequest: prs.length > 0
            };
          })
        );

        const demandesWithPr = prLinkedDemandes.filter(item => item.hasPullRequest);
        const automatedDemandesCount = demandesWithPr.filter(
          item => String(item.automatedTestsStatus || '').trim().toLowerCase() === 'realized'
        ).length;
        const demandesWithPrCount = demandesWithPr.length;
        const automatedPercentage = demandesWithPrCount
          ? Number(((automatedDemandesCount / demandesWithPrCount) * 100).toFixed(1))
          : 0;

        setAutomatedCoverageStats({
          totalEligible: automatedKpiItems.length,
          withPr: demandesWithPrCount,
          automated: automatedDemandesCount,
          percentage: automatedPercentage
        });
      } catch (err) {
        console.error('Erreur chargement demandes client:', err);
        setError(err.message || 'Erreur chargement');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client</h2>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Erreur : {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client créées par sprint</h2>
        {bySprint ? (
          <Bar
            data={bySprint}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false }
              },
              scales: {
                x: { title: { display: true, text: 'Sprint' } },
                y: { title: { display: true, text: 'Nombre' }, beginAtZero: true }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client par statut</h2>
        {byStatus ? (
          <Bar
            data={byStatus}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false }
              },
              scales: {
                x: { title: { display: true, text: 'Statut' } },
                y: { title: { display: true, text: 'Nombre' }, beginAtZero: true }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client par type</h2>
        {byType ? (
          <Bar
            data={byType}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false }
              },
              scales: {
                x: { title: { display: true, text: 'Type' } },
                y: { title: { display: true, text: 'Nombre' }, beginAtZero: true }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client par area path</h2>
        {byAreaPath ? (
          <Bar
            data={byAreaPath}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false }
              },
              scales: {
                x: { title: { display: true, text: 'Area Path' } },
                y: { title: { display: true, text: 'Nombre' }, beginAtZero: true }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">MTTR des demandes client par type</h2>
        {mttrByType ? (
          <Bar
            data={mttrByType}
            options={{
              responsive: true,
              plugins: {
                legend: { display: true, position: 'top' },
                title: { display: false },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const datasetLabel = context.dataset.label;
                      const year = datasetLabel.includes('2025') ? 2025 : 2026;
                      const counts = year === 2025 ? mttrByType.counts2025 : mttrByType.counts2026;
                      const count = counts ? counts[context.dataIndex] : 0;
                      return `${datasetLabel}: ${context.parsed.y} jours (tickets: ${count})`;
                    }
                  }
                }
              },
              scales: {
                x: { title: { display: true, text: 'Type' } },
                y: { title: { display: true, text: 'MTTR (jours)' }, beginAtZero: true }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
        {mttrByType && mttrByType.labels.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Tickets 2025:</strong> {mttrByType.labels.map((label, index) => `${label} (${mttrByType.counts2025[index] || 0})`).join(' | ')}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Tickets 2026:</strong> {mttrByType.labels.map((label, index) => `${label} (${mttrByType.counts2026[index] || 0})`).join(' | ')}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Demandes client automatisées</h2>
        {automatedCoverageStats ? (
          <>
            <Bar
              data={{
                labels: ['Demandes avec au moins une PR', 'Demandes automatisées'],
                datasets: [
                  {
                    label: 'Nombre',
                    data: [automatedCoverageStats.withPr, automatedCoverageStats.automated],
                    backgroundColor: [
                      'rgba(99, 102, 241, 0.7)',
                      'rgba(16, 185, 129, 0.7)'
                    ]
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                  title: { display: false },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        if (context.dataIndex === 1) {
                          return `${context.parsed.y} (${automatedCoverageStats.percentage}% des demandes avec PR)`;
                        }
                        return `${context.parsed.y} demandes`;
                      }
                    }
                  }
                },
                scales: {
                  x: { title: { display: true, text: 'Périmètre' } },
                  y: { title: { display: true, text: 'Nombre' }, beginAtZero: true }
                }
              }}
            />
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              <div>
                Automatisées: <strong>{automatedCoverageStats.automated}</strong> / <strong>{automatedCoverageStats.withPr}</strong> demandes avec au moins une PR ({automatedCoverageStats.percentage}%)
              </div>
              <div>
                Périmètre analysé: demandes client créées depuis le {AUTOMATED_KPI_START_DATE}, avec statut ≥ {AUTOMATED_KPI_MIN_STATE}
              </div>
              <div>
                Demandes éligibles avant filtre PR: {automatedCoverageStats.totalEligible}
              </div>
            </div>
          </>
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Repartition PR par type (30 dernieres demandes done)</h2>
        {prTypeStats ? (
          <Bar
            data={{
              labels: ['Back', 'Front', 'DevOps'],
              datasets: [
                {
                  label: 'Pourcentage',
                  data: [
                    prTypeStats.percentages.back,
                    prTypeStats.percentages.front,
                    prTypeStats.percentages.devops
                  ],
                  backgroundColor: [
                    'rgba(59, 130, 246, 0.7)',
                    'rgba(16, 185, 129, 0.7)',
                    'rgba(234, 179, 8, 0.7)'
                  ]
                }
              ]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const label = context.label.toLowerCase();
                      const count = prTypeStats.counts[label] || 0;
                      return `${context.parsed.y}% (tickets: ${count})`;
                    }
                  }
                }
              },
              scales: {
                x: { title: { display: true, text: 'Type' } },
                y: { title: { display: true, text: 'Pourcentage' }, beginAtZero: true, max: 100 }
              }
            }}
          />
        ) : (
          <div className="text-gray-500">Aucune donnee</div>
        )}
        {prTypeStats && (
          <div className="mt-4 text-sm text-gray-600">
            Total analyse: {prTypeStats.total} demandes done
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-sm text-gray-600">
          Perimetre: La Fabrique
        </div>
        <div className="text-sm text-gray-600">
          Champ type: {TYPE_FIELD_NAME}
        </div>
        <div className="text-sm text-gray-600">
          Total demandes: {items.length}
        </div>
      </div>
    </div>
  );
}
